<template>
    <div
        class="alarm-type-selector"
        data-design-tokens="1.0.0"
        data-theme="light"
    >
        <button
            class="selector-trigger"
            :class="{ open: panelOpen }"
            type="button"
            :title="displayLabel"
            @click="toggleSelector"
        >
            <span class="selector-label">{{ displayLabel }}</span>
            <i class="icon-co_down selector-arrow"></i>
        </button>

        <div v-if="panelOpen && !isMobile" class="desktop-panel">
            <div class="desktop-options-viewport">
                <div
                    ref="desktopOptionsScroll"
                    class="desktop-options-scroll"
                    @scroll="updateDesktopScrollbar"
                >
                    <alarm-type-options
                        :options="options"
                        :selectedKeys="draftKeys"
                        @change="draftKeys = $event"
                        @layout-change="updateDesktopScrollbar"
                    />
                </div>
                <div
                    v-if="desktopScrollbarVisible"
                    class="desktop-scrollbar-track"
                    @mousedown="handleDesktopScrollbarTrackMouseDown"
                >
                    <span
                        class="desktop-scrollbar-thumb"
                        :style="desktopScrollbarThumbStyle"
                        @mousedown.stop.prevent="startDesktopScrollbarDrag"
                    ></span>
                </div>
            </div>
            <div class="desktop-footer">
                <button class="reset-button" type="button" @click="resetDraft">
                    {{ $t( 'cloud.alarmTypeSelector.reset' ) }}
                </button>
                <button class="confirm-button" type="button" @click="commitAndClose">
                    {{ $t( 'dialog.ok' ) }}
                </button>
            </div>
        </div>

        <reo-dialog
            v-if="panelOpen && isMobile"
            class="alarm-type-mobile-dialog"
            :title="$t( 'cloud.alarmTypeSelector.eventType' )"
            :showButton="false"
            isBottomWhenMobile
            @cancel="cancelMobile"
        >
            <alarm-type-options
                :options="options"
                :selectedKeys="draftKeys"
                @change="draftKeys = $event"
            />
            <template #note>
                <div class="mobile-footer">
                    <button class="confirm-button" type="button" @click="commitAndClose">
                        {{ $t( 'dialog.ok' ) }}
                    </button>
                    <button class="reset-button" type="button" @click="resetDraft">
                        {{ $t( 'cloud.alarmTypeSelector.reset' ) }}
                    </button>
                </div>
            </template>
        </reo-dialog>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import '@reolink-web/components.design-tokens/css/reolink';
import ReoDialog from '@/basic/Dialog.vue';
import AlarmTypeOptions from './AlarmTypeOptions.vue';
import {
    expandAlarmTypeSelection,
    getAlarmTypeSelectionLabels,
    IAlarmTypeTreeOption,
    serializeAlarmTypeSelection
} from '../../lib/alarmTypeSelector';

const MIN_DESKTOP_SCROLLBAR_THUMB_HEIGHT = 24;

const clamp = ( value: number, min: number, max: number ): number =>
    Math.min( Math.max( value, min ), max );

export default Vue.extend({
    name: 'AlarmTypeSelector',
    components: {
        AlarmTypeOptions,
        ReoDialog
    },
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        value: {
            type: Array as PropType<string[]>,
            required: true
        },
        options: {
            type: Array as PropType<IAlarmTypeTreeOption[]>,
            required: true
        }
    },
    data() {

        return {
            panelOpen: false,
            draftKeys: [] as string[],
            desktopScrollbarVisible: false,
            desktopScrollbarThumbHeight: 0,
            desktopScrollbarThumbOffset: 0,
            desktopScrollbarDragStartY: 0,
            desktopScrollbarDragStartOffset: 0
        };

    },
    computed: {
        isMobile(): boolean {

            return this.$store.getters.isMobile;

        },
        displayLabel(): string {

            const displayValue = this.panelOpen
                ? serializeAlarmTypeSelection( this.draftKeys, this.options )
                : this.value;
            const labels = getAlarmTypeSelectionLabels( displayValue, this.options );
            return labels.length > 0
                ? labels.join( '/' )
                : this.$t( 'cloud.alarmTypeSelector.allEventTypes' ) as string;

        },
        desktopScrollbarThumbStyle(): Record<string, string> {

            return {
                height: `${this.desktopScrollbarThumbHeight}px`,
                transform: `translateY(${this.desktopScrollbarThumbOffset}px)`
            };

        }
    },
    mounted() {

        document.addEventListener( 'mousedown', this.handleDocumentMouseDown );

    },
    destroyed() {

        document.removeEventListener( 'mousedown', this.handleDocumentMouseDown );
        document.removeEventListener( 'mousemove', this.handleDesktopScrollbarDrag );
        document.removeEventListener( 'mouseup', this.stopDesktopScrollbarDrag );
        if ( this.panelOpen ) this.$emit( 'open-change', false );

    },
    methods: {
        setPanelOpen( open: boolean ): void {

            if ( this.panelOpen === open ) return;
            this.panelOpen = open;
            this.$emit( 'open-change', open );

            if ( !open ) {

                this.desktopScrollbarVisible = false;
                this.stopDesktopScrollbarDrag();

            }

        },
        openSelector(): void {

            this.draftKeys = expandAlarmTypeSelection( this.value, this.options );
            this.setPanelOpen( true );

            if ( !this.isMobile ) {

                this.$nextTick(() => {

                    this.updateDesktopScrollbar();

                });

            }

        },
        toggleSelector(): void {

            if ( !this.panelOpen ) {

                this.openSelector();

            } else if ( !this.isMobile ) {

                this.commitAndClose();

            }

        },
        resetDraft(): void {

            this.draftKeys = [];

        },
        commitAndClose(): void {

            const value = serializeAlarmTypeSelection( this.draftKeys, this.options );
            this.$emit( 'input', value );
            this.$emit( 'change', value );
            this.setPanelOpen( false );

        },
        cancelMobile(): void {

            this.setPanelOpen( false );

        },
        updateDesktopScrollbar(): void {

            const scrollElement = this.$refs.desktopOptionsScroll as HTMLElement | undefined;

            if ( !scrollElement ) {

                this.desktopScrollbarVisible = false;
                return;

            }

            const { clientHeight, scrollHeight, scrollTop } = scrollElement;
            const scrollableHeight = scrollHeight - clientHeight;

            if ( clientHeight <= 0 || scrollableHeight <= 1 ) {

                this.desktopScrollbarVisible = false;
                this.desktopScrollbarThumbHeight = 0;
                this.desktopScrollbarThumbOffset = 0;
                return;

            }

            const thumbHeight = Math.min(
                clientHeight,
                Math.max(
                    MIN_DESKTOP_SCROLLBAR_THUMB_HEIGHT,
                    clientHeight * clientHeight / scrollHeight
                )
            );
            const maxThumbOffset = clientHeight - thumbHeight;

            this.desktopScrollbarVisible = true;
            this.desktopScrollbarThumbHeight = thumbHeight;
            this.desktopScrollbarThumbOffset = maxThumbOffset * scrollTop / scrollableHeight;

        },
        scrollDesktopOptionsToThumbOffset( thumbOffset: number ): void {

            const scrollElement = this.$refs.desktopOptionsScroll as HTMLElement | undefined;
            if ( !scrollElement ) return;

            const maxThumbOffset = scrollElement.clientHeight - this.desktopScrollbarThumbHeight;
            const scrollableHeight = scrollElement.scrollHeight - scrollElement.clientHeight;
            if ( maxThumbOffset <= 0 || scrollableHeight <= 0 ) return;

            scrollElement.scrollTop = clamp( thumbOffset, 0, maxThumbOffset )
                / maxThumbOffset
                * scrollableHeight;

        },
        handleDesktopScrollbarTrackMouseDown( event: MouseEvent ): void {

            if ( event.button !== 0 ) return;

            const trackElement = event.currentTarget as HTMLElement;
            const thumbOffset = event.clientY
                - trackElement.getBoundingClientRect().top
                - this.desktopScrollbarThumbHeight / 2;

            this.scrollDesktopOptionsToThumbOffset( thumbOffset );

        },
        startDesktopScrollbarDrag( event: MouseEvent ): void {

            if ( event.button !== 0 ) return;

            this.desktopScrollbarDragStartY = event.clientY;
            this.desktopScrollbarDragStartOffset = this.desktopScrollbarThumbOffset;
            document.addEventListener( 'mousemove', this.handleDesktopScrollbarDrag );
            document.addEventListener( 'mouseup', this.stopDesktopScrollbarDrag );

        },
        handleDesktopScrollbarDrag( event: MouseEvent ): void {

            event.preventDefault();
            this.scrollDesktopOptionsToThumbOffset(
                this.desktopScrollbarDragStartOffset
                + event.clientY
                - this.desktopScrollbarDragStartY
            );

        },
        stopDesktopScrollbarDrag(): void {

            document.removeEventListener( 'mousemove', this.handleDesktopScrollbarDrag );
            document.removeEventListener( 'mouseup', this.stopDesktopScrollbarDrag );

        },
        handleDocumentMouseDown( event: MouseEvent ): void {

            if (
                this.panelOpen &&
                !this.isMobile &&
                !this.$el.contains( event.target as Node )
            ) {

                this.commitAndClose();

            }

        }
    },
    watch: {
        options(): void {

            if ( this.panelOpen ) {

                this.draftKeys = expandAlarmTypeSelection( this.value, this.options );
                this.$nextTick(() => {

                    this.updateDesktopScrollbar();

                });

            }

        }
    }
});
</script>

<style lang="less" scoped>
@import (reference) '@reolink-web/components.design-tokens/less';

.alarm-type-selector {
    position: relative;
    width: 200px;
    color: @font_primary;
    font-family: @font_01;
    text-align: left;
}

button {
    padding: 0;
    border: 0;
    font: inherit;
    cursor: pointer;
}

.selector-trigger {
    display: flex;
    width: 100%;
    height: @spacing_7xl;
    padding: 0 @spacing_xl;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    border: 1px solid @font_tertiary;
    border-radius: @radius_xs;
    color: @font_primary;
    background: transparent;

    &:hover,
    &.open {
        border-color: @b_6;
    }
}

.selector-label {
    .body_4_400();
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.selector-arrow {
    flex: 0 0 auto;
    margin-left: @spacing_md;
    font-size: 16px;
    transition: transform .2s ease;
}

.selector-trigger.open .selector-arrow {
    transform: rotate(180deg);
}

.desktop-panel {
    position: absolute;
    z-index: 50;
    top: calc(100% + @spacing_sm);
    left: 0;
    width: 240px;
    padding: @spacing_2xl;
    box-sizing: border-box;
    overflow: hidden;
    border: 1px solid @line;
    border-radius: @radius_xs;
    background: @bg_01;
    box-shadow: @shadow_2;
}

.desktop-options-viewport {
    position: relative;
}

.desktop-options-scroll {
    width: 100%;
    max-height: 326px;
    overflow-x: hidden;
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }
}

.desktop-scrollbar-track {
    position: absolute;
    z-index: 1;
    top: 0;
    right: calc(1px - @spacing_2xl);
    width: @spacing_xl;
    height: 100%;
    cursor: pointer;
}

.desktop-scrollbar-thumb {
    position: absolute;
    top: 0;
    right: 0;
    display: block;
    width: @spacing_xs;
    min-height: @spacing_4xl;
    border-radius: @radius_full;
    background: @font_tertiary;
    cursor: grab;
    user-select: none;

    &:active {
        cursor: grabbing;
    }
}

.desktop-footer,
.mobile-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: @spacing_xl;
}

.desktop-footer {
    padding-top: @spacing_xl;
    border-top: 1px solid @line;

    .reset-button,
    .confirm-button {
        .body_5_400();
        display: flex;
        height: @spacing_5xl;
        min-width: 72px;
        flex: 1 1 0;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: 0 @spacing_2xl;
    }
}

.reset-button {
    .label_1_400();
    color: @b_6;
    background: transparent;
}

.confirm-button {
    .label_1_500();
    min-width: 96px;
    padding: 7px @spacing_3xl;
    border-radius: @radius_sm;
    color: @white;
    background: @b_6;

    &:hover {
        background: @b_5;
    }
}

.alarm-type-mobile-dialog.dialog.reo-dialog {
    /deep/ .reo-dialog-inner {
        height: auto;
        max-height: 90vh;
        max-height: 90dvh;
        padding: @spacing_4xl @spacing_3xl @spacing_2xl;
        background: @bg_02;
    }

    /deep/ .dialog-title-container {
        flex-shrink: 0;
    }

    /deep/ .reo-dialog-inner p.dialog-title {
        .heading_6_600();
        margin-bottom: @spacing_xl;
    }

    /deep/ .scroll-context {
        flex: 0 1 auto;
        min-height: 0;
        overflow-y: auto;
    }
}

.mobile-footer {
    flex-shrink: 0;
    flex-direction: column;
    align-items: stretch;
    gap: @spacing_md;
    padding-top: @spacing_2xl;
}

.mobile-footer .reset-button {
    .body_4_500();
    width: 100%;
    min-width: 0;
    min-height: @spacing_7xl;
}

.mobile-footer .confirm-button {
    .body_4_500();
    width: 100%;
    min-width: 0;
    min-height: @spacing_7xl;
}

@media screen and (max-width: 1199px) {
    .alarm-type-selector {
        width: 100%;
    }

    .selector-trigger {
        height: 36px;
    }

    .selector-label {
        .body_4_400();
    }
}
</style>
